import React, { createContext, useContext, ReactNode, useEffect } from 'react';
import { useAIStrategyPersistence } from '../hooks/useAIStrategyPersistence';
import { useAITrading } from './AITradingContext';
import { AIStrategyConfig } from '../services/AIStrategyPersistenceService';

interface AIStrategyPersistenceContextType {
  // Persistence state
  activeStrategies: { [symbol: string]: AIStrategyConfig };
  isAITradingActive: boolean;
  isLoading: boolean;
  error: string | null;
  
  // Persistence actions
  saveStrategy: (symbol: string, strategyConfig: AIStrategyConfig) => Promise<void>;
  loadStrategy: (symbol: string) => Promise<AIStrategyConfig | null>;
  deactivateStrategy: (symbol: string) => Promise<void>;
  loadAllStrategies: () => Promise<void>;
  clearAllStrategies: () => Promise<void>;
  
  // Utilities
  getActiveSymbols: () => string[];
  isStrategyActive: (symbol: string) => boolean;
  getStrategyForSymbol: (symbol: string) => AIStrategyConfig | null;
  
  // Auto-restore functionality
  restoreStrategyForSymbol: (symbol: string) => Promise<void>;
  restoreAllStrategies: () => Promise<void>;
}

const AIStrategyPersistenceContext = createContext<AIStrategyPersistenceContextType | undefined>(undefined);

interface AIStrategyPersistenceProviderProps {
  children: ReactNode;
}

export const AIStrategyPersistenceProvider: React.FC<AIStrategyPersistenceProviderProps> = ({ children }) => {
  const persistenceHook = useAIStrategyPersistence();
  const { 
    updateStrategy, 
    startStrategy, 
    stopStrategy,
    updateConfig,
    getStrategy 
  } = useAITrading();
  
  /**
   * Restore strategy for a specific symbol
   * This will load the saved strategy and start AI trading if it was active
   */
  const restoreStrategyForSymbol = async (symbol: string): Promise<void> => {
    try {
      // Load strategy from persistence
      const savedStrategy = await persistenceHook.loadStrategy(symbol);
      
      if (!savedStrategy) {
        return;
      }
      
      // Update frontend AI trading context with saved configuration
      updateStrategy(symbol, {
        strategy: savedStrategy.type,
        config: savedStrategy.config,
        isRunning: savedStrategy.isActive, // Start if it was active
        signal: null
      });
      
      // If strategy was active, start AI trading
      if (savedStrategy.isActive) {
        startStrategy(symbol);
      }
      
    } catch (error) {
      console.error(`❌ Error restoring strategy for ${symbol}:`, error);
    }
  };
  
  /**
   * Restore all saved strategies
   * This will load all saved strategies and start AI trading for active ones
   */
  const restoreAllStrategies = async (): Promise<void> => {
    try {
      // Load all strategies from persistence
      await persistenceHook.loadAllStrategies();
      
      // Get all active symbols
      const activeSymbols = persistenceHook.getActiveSymbols();
      
      if (activeSymbols.length === 0) {
        return;
      }
      
      // Restore each strategy
      for (const symbol of activeSymbols) {
        await restoreStrategyForSymbol(symbol);
      }
      
    } catch (error) {
      console.error(`❌ Error restoring all strategies:`, error);
    }
  };
  
  /**
   * Simple save strategy to Firestore
   */
  const saveStrategy = async (symbol: string, strategyConfig: AIStrategyConfig): Promise<void> => {
    try {
      // Save to persistence service
      await persistenceHook.saveStrategy(symbol, strategyConfig);
      
      // Update frontend AI trading context
      updateStrategy(symbol, {
        strategy: strategyConfig.type,
        config: strategyConfig.config,
        isRunning: strategyConfig.isActive,
        signal: null
      });
      
      
    } catch (error) {
      console.error(`❌ Error saving strategy for ${symbol}:`, error);
      throw error;
    }
  };
  
  /**
   * Enhanced deactivate strategy that also stops AI trading
   */
  const deactivateStrategy = async (symbol: string): Promise<void> => {
    try {
      // Stop frontend AI trading for this symbol
      stopStrategy(symbol);
      
      // Remove from persistence
      await persistenceHook.deactivateStrategy(symbol);
      
      // Note: Backend strategy deactivation would need to be implemented
      // based on the specific backend API requirements
      
      
    } catch (error) {
      console.error(`❌ Error deactivating strategy for ${symbol}:`, error);
      throw error;
    }
  };
  
  /**
   * Auto-restore strategies on mount
   */
  useEffect(() => {
    // Small delay to ensure other contexts are initialized
    const timer = setTimeout(() => {
      restoreAllStrategies();
    }, 1000);
    
    return () => clearTimeout(timer);
  }, []); // Only run once on mount
  
  const contextValue: AIStrategyPersistenceContextType = {
    // Persistence state
    activeStrategies: persistenceHook.activeStrategies,
    isAITradingActive: persistenceHook.isAITradingActive,
    isLoading: persistenceHook.isLoading,
    error: persistenceHook.error,
    
    // Persistence actions
    saveStrategy,
    loadStrategy: persistenceHook.loadStrategy,
    deactivateStrategy,
    loadAllStrategies: persistenceHook.loadAllStrategies,
    clearAllStrategies: persistenceHook.clearAllStrategies,
    
    // Utilities
    getActiveSymbols: persistenceHook.getActiveSymbols,
    isStrategyActive: persistenceHook.isStrategyActive,
    getStrategyForSymbol: persistenceHook.getStrategyForSymbol,
    
    // Auto-restore functionality
    restoreStrategyForSymbol,
    restoreAllStrategies
  };
  
  return (
    <AIStrategyPersistenceContext.Provider value={contextValue}>
      {children}
    </AIStrategyPersistenceContext.Provider>
  );
};

export const useAIStrategyPersistenceContext = (): AIStrategyPersistenceContextType => {
  const context = useContext(AIStrategyPersistenceContext);
  if (context === undefined) {
    throw new Error('useAIStrategyPersistenceContext must be used within an AIStrategyPersistenceProvider');
  }
  return context;
};
